/**
 * O2 Slider — shared behavior for horizontal product sliders.
 *
 * Provides two custom elements used by featured-collection-o2 and
 * collection-slider-o2:
 *
 *   <o2-slider-progress data-slider-id="...">
 *     Draggable scroll-progress bar that mirrors slider scroll position.
 *
 *   <o2-slider-button data-slider-id="..."
 *                      data-direction="prev|next"   (default: next)
 *                      data-loop="true|false"       (default: false)
 *                      data-end-fade="true|false">  (default: false)
 *     Arrow control. `loop` wraps around at the ends. `end-fade` (next-only)
 *     hides the button at the end and toggles `.is-end` on the slider wrapper
 *     to drive the edge gradient.
 */

(() => {
  // Read live so OS-level changes take effect without reload.
  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const scrollBehavior = () => (prefersReducedMotion() ? 'auto' : 'smooth');

  const sliderScrollDuration = () =>
    Number.parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue('--o2-duration-slider-scroll')
    ) || 200;

  const cancelSliderAnimation = (slider) => {
    if (!slider?.o2ScrollAnimationFrame) return;

    cancelAnimationFrame(slider.o2ScrollAnimationFrame);
    slider.o2ScrollAnimationFrame = undefined;
    slider.o2ScrollTarget = undefined;
    slider.classList.remove('is-programmatic-scrolling');
  };

  const scrollSliderTo = (slider, requestedTarget) => {
    const maxScroll = slider.scrollWidth - slider.clientWidth;
    const target = Math.max(0, Math.min(maxScroll, requestedTarget));
    const isMobile = window.O2Breakpoints?.matches('mobileOnly');

    if (!isMobile || prefersReducedMotion()) {
      slider.scrollTo({ left: target, behavior: scrollBehavior() });
      return;
    }

    if (slider.o2ScrollAnimationFrame) {
      cancelAnimationFrame(slider.o2ScrollAnimationFrame);
    }

    const start = slider.scrollLeft;
    const distance = target - start;
    const duration = sliderScrollDuration();
    const startTime = performance.now();

    if (Math.abs(distance) < 1) return;

    slider.o2ScrollTarget = target;
    slider.classList.add('is-programmatic-scrolling');

    const animate = (timestamp) => {
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 4);

      slider.scrollLeft = start + distance * easedProgress;

      if (progress < 1) {
        slider.o2ScrollAnimationFrame = requestAnimationFrame(animate);
        return;
      }

      slider.scrollLeft = target;
      slider.o2ScrollAnimationFrame = undefined;
      slider.o2ScrollTarget = undefined;
      slider.classList.remove('is-programmatic-scrolling');
    };

    slider.o2ScrollAnimationFrame = requestAnimationFrame(animate);
  };

class O2SliderProgress extends HTMLElement {
  constructor() {
    super();
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onDragStart = this.onDragStart.bind(this);
    this.onDragMove = this.onDragMove.bind(this);
    this.onDragEnd = this.onDragEnd.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.isDragging = false;
  }

  connectedCallback() {
    this.sliderId = this.dataset.sliderId;
    this.slider = document.getElementById(this.sliderId);
    this.track = this.querySelector('.o2-progress-bar-track');
    this.rail = this.querySelector('.o2-progress-bar-rail');
    this.thumb = this.querySelector('.o2-progress-bar-thumb');
    this.controls = this.closest('.o2-slider-controls');

    if (this.slider) {
      this.init();
    } else {
      requestAnimationFrame(() => {
        this.slider = document.getElementById(this.sliderId);
        if (this.slider) this.init();
      });
    }
  }

  init() {
    if (!this.slider || !this.track || !this.rail || !this.thumb) return;

    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    // Drag events on the track (handles both click-jump and drag)
    this.track.addEventListener('pointerdown', this.onDragStart);
    this.track.addEventListener('keydown', this.onKeyDown);

    // Reveal controls only after the first overflow calculation succeeds.
    requestAnimationFrame(() => {
      this.updateProgress();
      this.controls?.removeAttribute('hidden');
    });
    setTimeout(() => this.updateProgress(), 100);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.updateProgress();
        observer.disconnect();
      }
    });
    observer.observe(this.slider);
  }

  disconnectedCallback() {
    this.controls?.setAttribute('hidden', '');
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.onScroll);
    }
    window.removeEventListener('resize', this.onResize);
    if (this.track) {
      this.track.removeEventListener('pointerdown', this.onDragStart);
      this.track.removeEventListener('pointermove', this.onDragMove);
      this.track.removeEventListener('pointerup', this.onDragEnd);
      this.track.removeEventListener('pointercancel', this.onDragEnd);
      this.track.removeEventListener('keydown', this.onKeyDown);
    }
  }

  onScroll() {
    requestAnimationFrame(() => this.updateProgress());
  }

  onResize() {
    requestAnimationFrame(() => this.updateProgress());
  }

  onDragStart(e) {
    if (this.isDragging) return;

    e.preventDefault(); // Prevent text selection
    this.isDragging = true;
    this.activePointerId = e.pointerId;
    this.thumb.classList.add('dragging');
    this.track.focus({ preventScroll: true });
    // Disable scroll snapping for smooth dragging
    this.slider.classList.add('is-dragging');

    const thumbRect = this.thumb.getBoundingClientRect();
    this.dragOffset =
      e.target === this.thumb ? e.clientX - thumbRect.left : thumbRect.width / 2;

    this.track.setPointerCapture(e.pointerId);
    this.track.addEventListener('pointermove', this.onDragMove);
    this.track.addEventListener('pointerup', this.onDragEnd);
    this.track.addEventListener('pointercancel', this.onDragEnd);

    // Handle initial click/tap position smoothly
    this.handleDrag(e, true);
  }

  onDragMove(e) {
    if (!this.isDragging || e.pointerId !== this.activePointerId) return;
    e.preventDefault(); // Prevent page scrolling on touch
    this.handleDrag(e, false); // Instant updates during drag
  }

  onDragEnd(e) {
    if (e.pointerId !== this.activePointerId) return;

    this.isDragging = false;
    this.thumb.classList.remove('dragging');

    if (this.track.hasPointerCapture(e.pointerId)) {
      this.track.releasePointerCapture(e.pointerId);
    }
    this.track.removeEventListener('pointermove', this.onDragMove);
    this.track.removeEventListener('pointerup', this.onDragEnd);
    this.track.removeEventListener('pointercancel', this.onDragEnd);
    this.activePointerId = undefined;

    this.snapToNearestSlide();
  }

  onKeyDown(event) {
    if (!this.slider) return;

    const firstSlide = this.slider.querySelector('.o2-slide');
    if (!firstSlide) return;

    const style = window.getComputedStyle(this.slider);
    const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    const scrollAmount = firstSlide.offsetWidth + gap;
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    let targetScroll;

    if (event.key === 'ArrowLeft') targetScroll = this.slider.scrollLeft - scrollAmount;
    if (event.key === 'ArrowRight') targetScroll = this.slider.scrollLeft + scrollAmount;
    if (event.key === 'PageUp') targetScroll = this.slider.scrollLeft - scrollAmount;
    if (event.key === 'PageDown') targetScroll = this.slider.scrollLeft + scrollAmount;
    if (event.key === 'Home') targetScroll = 0;
    if (event.key === 'End') targetScroll = maxScroll;
    if (targetScroll === undefined) return;

    event.preventDefault();
    this.slider.scrollTo({
      left: Math.max(0, Math.min(maxScroll, targetScroll)),
      behavior: 'auto',
    });
  }

  snapToNearestSlide() {
    if (!this.slider) return;

    const currentScroll = this.slider.scrollLeft;
    let minDiff = Infinity;
    let targetScroll = currentScroll;

    const slides = Array.from(this.slider.children).filter((el) => el.tagName === 'LI');

    slides.forEach((slide) => {
      const slideScrollPosition =
        slide.getBoundingClientRect().left -
        this.slider.getBoundingClientRect().left +
        currentScroll;
      const diff = Math.abs(slideScrollPosition - currentScroll);
      if (diff < minDiff) {
        minDiff = diff;
        targetScroll = slideScrollPosition;
      }
    });

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    if (Math.abs(maxScroll - currentScroll) < minDiff) {
      targetScroll = maxScroll;
    }

    this.slider.scrollTo({
      left: targetScroll,
      behavior: scrollBehavior(),
    });

    // Re-enable native snap after the scroll settles.
    setTimeout(() => {
      this.slider.classList.remove('is-dragging');
    }, 500);
  }

  handleDrag(e, smooth = false) {
    const trackRect = this.rail.getBoundingClientRect();
    const thumbWidth = this.thumb.getBoundingClientRect().width;
    const availableWidth = Math.max(trackRect.width - thumbWidth, 1);

    let percentage = (e.clientX - trackRect.left - this.dragOffset) / availableWidth;
    percentage = Math.max(0, Math.min(1, percentage));

    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const targetScroll = percentage * maxScroll;

    if (smooth) {
      this.slider.scrollTo({
        left: targetScroll,
        behavior: scrollBehavior(),
      });
    } else {
      this.slider.scrollLeft = targetScroll;
      this.updateProgress();
    }
  }

  updateProgress() {
    if (!this.slider || !this.thumb) return;

    const sliderWidth = this.slider.clientWidth;
    const scrollWidth = this.slider.scrollWidth;
    const scrollLeft = this.slider.scrollLeft;

    if (scrollWidth <= sliderWidth + 1) {
      this.style.display = 'none';
      this.controls?.classList.add('is-hidden');
      return;
    } else {
      this.style.display = 'block';
    }

    const proportional = this.dataset.proportional === 'true';
    const thumbWidthPercentage = Math.max(
      (sliderWidth / scrollWidth) * (proportional ? 100 : 75),
      20
    );
    const trackScrollableWidth = 100 - thumbWidthPercentage;

    const maxScroll = scrollWidth - sliderWidth;
    const scrollProgress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    const clampedProgress = Math.min(Math.max(scrollProgress, 0), 1);

    const thumbPosition = clampedProgress * trackScrollableWidth;

    this.thumb.style.width = `${thumbWidthPercentage}%`;
    const translatePercentage = (thumbPosition / thumbWidthPercentage) * 100;
    this.thumb.style.transform = `translateX(${translatePercentage}%)`;
    this.rail.style.setProperty('--o2-progress-size', `${thumbWidthPercentage}%`);
    this.rail.style.setProperty('--o2-progress-offset', `${thumbPosition}%`);
    this.track.setAttribute('aria-valuenow', Math.round(clampedProgress * 100));
    this.controls?.classList.remove('is-hidden');
  }
}

if (!customElements.get('o2-slider-progress')) {
  customElements.define('o2-slider-progress', O2SliderProgress);
}

class O2SliderButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.sliderId = this.dataset.sliderId;
    this.direction = this.dataset.direction || 'next';
    this.loop = this.dataset.loop === 'true';
    this.endFade = this.dataset.endFade === 'true';
    this.onButtonClick = this.onButtonClick.bind(this);
    this.checkVisibility = this.checkVisibility.bind(this);
    this.cancelAnimation = this.cancelAnimation.bind(this);
  }

  connectedCallback() {
    if (!this.button || !this.sliderId) return;

    this.slider = document.getElementById(this.sliderId);
    if (!this.slider) return;

    this.button.addEventListener('click', this.onButtonClick);
    this.slider.addEventListener('scroll', this.checkVisibility, { passive: true });
    this.slider.addEventListener('pointerdown', this.cancelAnimation, { passive: true });
    window.addEventListener('resize', this.checkVisibility, { passive: true });

    requestAnimationFrame(this.checkVisibility);
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }
    if (this.slider) {
      this.slider.removeEventListener('scroll', this.checkVisibility);
      this.slider.removeEventListener('pointerdown', this.cancelAnimation);
    }
    window.removeEventListener('resize', this.checkVisibility);
  }

  cancelAnimation() {
    cancelSliderAnimation(this.slider);
  }

  checkVisibility() {
    if (!this.slider) return;

    const sliderWidth = this.slider.clientWidth;
    const scrollWidth = this.slider.scrollWidth;
    const hasOverflow = scrollWidth > sliderWidth + 1;
    const isAtStart = this.slider.scrollLeft <= 2;
    const isAtEnd =
      this.slider.scrollLeft + this.slider.clientWidth >= this.slider.scrollWidth - 2;

    this.classList.toggle('is-hidden', !hasOverflow);

    if (!this.loop) {
      this.button.disabled =
        !hasOverflow || (this.direction === 'prev' ? isAtStart : isAtEnd);
    }

    if (this.endFade && this.direction === 'next') {
      // Hide at the end and toggle the wrapper edge gradient.
      this.classList.toggle('is-hidden', isAtEnd);
      this.slider.parentElement.classList.toggle('is-end', isAtEnd);
      return;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    const firstSlide = this.slider.querySelector('.o2-slide');
    if (!firstSlide) return;

    const slideWidth = firstSlide.offsetWidth;
    const style = window.getComputedStyle(this.slider);
    const gap = parseFloat(style.columnGap) || parseFloat(style.gap) || 0;
    const scrollAmount = slideWidth + gap;

    const scrollLeft = this.slider.o2ScrollTarget ?? this.slider.scrollLeft;
    const sliderWidth = this.slider.clientWidth;
    const scrollWidth = this.slider.scrollWidth;
    const tolerance = 5;
    let targetScroll;

    if (this.direction === 'prev') {
      if (this.loop && scrollLeft <= tolerance) {
        targetScroll = scrollWidth - sliderWidth;
      } else {
        targetScroll = scrollLeft - scrollAmount;
      }
    } else {
      if (this.loop && scrollLeft + sliderWidth >= scrollWidth - tolerance) {
        targetScroll = 0;
      } else {
        targetScroll = scrollLeft + scrollAmount;
      }
    }

    scrollSliderTo(this.slider, targetScroll);
  }
}

if (!customElements.get('o2-slider-button')) {
  customElements.define('o2-slider-button', O2SliderButton);
}
})();
